"use client";

import React from "react";
import { ShieldCheck, Mail, Lock, Cookie, EyeOff, FileSpreadsheet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PrivacyPolicy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="privacypolicy" data-nav="light" ref={ref} style={{
    opacity: isVisible ? 1 : 0
  }} className="bg-background text-foreground py-16 md:py-24 transition-opacity duration-700 ease-out">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground">
            Privacy policy
          </h1>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed">
            This policy outlines how Brookhaven Elevation Renovations handles the information you share when submitting estimate requests or browsing our website.
          </p>
        </div>

        {/* Highlight Banner / Image Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-14">
          <div className="lg:col-span-7 flex flex-col justify-between">
            <Card className="border border-border/80 bg-card shadow-sm h-full flex flex-col justify-between">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/15 text-secondary">
                    <ShieldCheck className="size-6 text-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-medium text-foreground">
                      Our commitment to your privacy
                    </CardTitle>
                    <p className="text-sm text-foreground/75 mt-0.5">
                      Clear, honest handling of your home project details.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm sm:text-base text-foreground/80 leading-relaxed pt-0">
                <p>
                  We treat your project plans and personal information with care. We collect details only to understand your remodeling goals, provide comprehensive project estimates, and maintain direct communication with you throughout your renovation inquiry.
                </p>
                <div className="rounded-lg bg-muted/60 p-4 border border-border/60">
                  <div className="flex items-start gap-3">
                    <EyeOff className="size-5 text-secondary mt-0.5 shrink-0" />
                    <p className="text-sm text-foreground font-medium">
                      We never sell, rent, or trade your contact or project details to third-party brokers or advertisers.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5 relative overflow-hidden rounded-xl border border-border min-h-[260px] lg:min-h-full">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/consultation_estimate_notes.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/consultation_estimate_notes.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/consultation_estimate_notes.webp-wvc-sizes" alt="Architectural renovation blueprints and consultation notes prepared for a residential project estimate" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-center" />
          </div>
        </div>

        <Separator className="my-10" />

        {/* Policy Detail Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-center gap-2.5">
              <FileSpreadsheet className="size-5 text-secondary shrink-0" />
              <h2 className="text-xl font-semibold text-foreground">
                Information we collect
              </h2>
            </div>
            <div className="md:col-span-8 space-y-3 text-foreground/85 leading-relaxed">
              <p>
                When you interact with our website or request a project estimate, we collect the personal information you voluntarily submit through our contact forms. This typically includes:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
                <li>Your full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Project details, including room types, scope, and specific remodeling goals</li>
              </ul>
              <p className="text-sm text-foreground/75 pt-1">
                You may browse our website without providing this information, but submitting an inquiry requires these contact points so we can respond accurately.
              </p>
            </div>
          </div>

          <Separator />

          {/* Section 2 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-center gap-2.5">
              <Lock className="size-5 text-secondary shrink-0" />
              <h2 className="text-xl font-semibold text-foreground">
                How we use your information
              </h2>
            </div>
            <div className="md:col-span-8 space-y-3 text-foreground/85 leading-relaxed">
              <p>
                The information you submit is used solely for standard business operations related to your home renovation request:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 marker:text-primary">
                <li>Responding promptly to your inquiries and consultation requests</li>
                <li>Preparing, scoping, and sending customized project estimates</li>
                <li>Communicating timelines, design ideas, and next steps for your home</li>
              </ul>
              <p className="text-sm text-foreground/75 pt-1">
                We only use the phone number and email address you supply to discuss your residential renovation project.
              </p>
            </div>
          </div>

          <Separator />

          {/* Section 3 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-center gap-2.5">
              <EyeOff className="size-5 text-secondary shrink-0" />
              <h2 className="text-xl font-semibold text-foreground">
                Information sharing
              </h2>
            </div>
            <div className="md:col-span-8 space-y-3 text-foreground/85 leading-relaxed">
              <p>
                Brookhaven Elevation Renovations does not sell, lease, exchange, or disclose your personal information to third parties or marketing brokers.
              </p>
              <p className="text-sm text-foreground/75">
                Your data is accessible only by authorized team members responsible for preparing estimates and managing project communications.
              </p>
            </div>
          </div>

          <Separator />

          {/* Section 4 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-center gap-2.5">
              <Cookie className="size-5 text-secondary shrink-0" />
              <h2 className="text-xl font-semibold text-foreground">
                Cookies and analytics
              </h2>
            </div>
            <div className="md:col-span-8 space-y-3 text-foreground/85 leading-relaxed">
              <p>
                Our website utilizes standard web cookies and anonymous analytics to monitor general traffic patterns, identify device compatibility, and improve page loading performance.
              </p>
              <p className="text-sm text-foreground/75">
                These analytic metrics do not track personally identifiable credentials and serve only to keep the website running efficiently for visitors exploring renovation ideas. You can disable cookies at any time via your browser settings.
              </p>
            </div>
          </div>

          <Separator />

          {/* Section 5: Contact */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-4 flex items-center gap-2.5">
              <Mail className="size-5 text-secondary shrink-0" />
              <h2 className="text-xl font-semibold text-foreground">
                Privacy questions
              </h2>
            </div>
            <div className="md:col-span-8 space-y-4 text-foreground/85 leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding this privacy policy or how Brookhaven Elevation Renovations manages your inquiry details, please contact us directly by email:
              </p>
              <div className="inline-flex items-center gap-3 p-3.5 rounded-lg border border-border bg-card">
                <Mail className="size-5 text-secondary shrink-0" />
                <a href="mailto:serviceleadalerts@gmail.com" className="font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  serviceleadalerts@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}